import { AxiosError } from 'axios';
import { api } from '@shared/api';
import { mapSenseContexts } from '@shared/lib';
import type { Language, Level, ModuleSummary, ModuleState, Word, WordMeaning } from '@shared/types';

export interface PublicModule {
  id: string;
  name: string;
  description: string;
  cefrLevel: Level | null;
  sourceLang: Language;
  targetLang: Language;
  /** Имя автора (владельца) модуля — чтобы различать одноимённые модули. */
  author: string | null;
  /** Модуль команды Lomora: владелец — системный аккаунт (не совпадение имени). */
  isOfficial: boolean;
  /** Сколько разных пользователей добавили модуль себе. */
  installs: number;
  /** Уже добавил ли текущий юзер этот модуль себе (форкнул). */
  isAdded: boolean;
  cards: Word[];
}

export class ForkModuleError extends Error {
  constructor(public code: 'ALREADY_ADDED' | 'NOT_FOUND' | 'NETWORK' | 'UNKNOWN') {
    super(code);
    this.name = 'ForkModuleError';
  }
}

export interface PublicModulePage {
  items: PublicModule[];
  total: number;
  page: number;
  pageSize: number;
  pages: number;
}

export interface FetchPublicModulesArgs {
  page?: number;
  pageSize?: number;
  search?: string;
  level?: Level | 'all';
  /** true — только модули Lomora, false — только сообщества, нет — все. */
  official?: boolean;
}

interface BackendTranslation {
  id: string;
  text: string;
  gender: string | null;
}

interface BackendSense {
  id: string;
  translations: BackendTranslation[];
  en_synonyms: string[];
  example_en: string | null;
  simple_example_en?: string | null;
  hint: string | null;
  definition?: string | null;
  selection_options: string[] | null;
  is_rare?: boolean;
  contexts?: unknown;
}

interface BackendMeaning {
  id: string;
  pos: string | null;
  senses: BackendSense[];
}

interface BackendCard {
  id: string;
  front_name: string;
  primary_translation: string | null;
  transcription: string | null;
  cefr: Level | null;
  meanings: BackendMeaning[];
}

function mapMeanings(meanings: BackendMeaning[] | null | undefined): WordMeaning[] {
  if (!Array.isArray(meanings)) return [];
  return meanings.map((m) => ({
    id: m.id,
    pos: m.pos ?? null,
    senses: (m.senses ?? []).map((s) => ({
      id: s.id,
      translations: (s.translations ?? []).map((t) => ({
        id: t.id,
        text: t.text,
        gender: t.gender ?? null,
      })),
      enSynonyms: s.en_synonyms ?? [],
      exampleEn: s.example_en ?? null,
      simpleExampleEn: s.simple_example_en ?? null,
      hint: s.hint ?? null,
      definition: s.definition ?? null,
      selectionOptions: s.selection_options ?? null,
      isRare: s.is_rare ?? false,
      contexts: mapSenseContexts(s.contexts),
    })),
  }));
}

function pickPrimary(card: BackendCard): string {
  if (card.primary_translation?.trim()) return card.primary_translation.trim();
  return card.meanings?.[0]?.senses?.[0]?.translations?.[0]?.text?.trim() ?? '';
}

interface BackendCardLink {
  card: BackendCard;
}

interface BackendModule {
  id: string;
  name: string;
  description: string | null;
  cefr_level: Level | null;
  source_lang: Language;
  target_lang: Language;
  is_public: boolean;
  status: 'active' | 'paused';
  is_seed: boolean;
  author?: string | null;
  is_added?: boolean;
  is_official?: boolean;
  installs?: number;
  card_links: BackendCardLink[];
}

interface BackendPage<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

function cardFromLink(link: BackendCardLink): Word {
  return {
    word: link.card.front_name,
    ipa: link.card.transcription ?? '',
    ru: pickPrimary(link.card),
    state: 'new',
    meanings: mapMeanings(link.card.meanings),
  };
}

function mapBackendModule(m: BackendModule): PublicModule {
  return {
    id: m.id,
    name: m.name,
    description: m.description?.trim() || '',
    cefrLevel: m.cefr_level,
    sourceLang: m.source_lang ?? 'en',
    targetLang: m.target_lang ?? 'ru',
    author: m.author ?? null,
    isOfficial: m.is_official ?? false,
    installs: m.installs ?? 0,
    isAdded: m.is_added ?? false,
    cards: m.card_links.map(cardFromLink),
  };
}

/** Ответ fork-а (созданный приватный модуль) → ModuleSummary для навигации/списка. */
function mapForkedToSummary(m: BackendModule): ModuleSummary {
  const cards = m.card_links.map(cardFromLink);
  const words = cards.length;
  return {
    id: m.id,
    title: m.name,
    desc: m.description?.trim() || 'Свой модуль',
    words,
    due: 0,
    fresh: words,
    progress: 0,
    state: (words > 0 ? 'progress' : 'new') as ModuleState,
    active: m.status === 'active',
    deleted: false,
    isPublic: m.is_public,
    preview: cards.slice(0, 6).map((c) => c.word),
    cards,
    sourceLang: m.source_lang ?? 'en',
    targetLang: m.target_lang ?? 'ru',
  };
}

const cache = new Map<string, PublicModule>();

export async function fetchPublicModules(args: FetchPublicModulesArgs = {}): Promise<PublicModulePage> {
  const page = Math.max(1, args.page ?? 1);
  const size = args.pageSize ?? 12;
  const params: Record<string, string | number | boolean> = {
    page,
    size,
    is_public: true,
  };
  const search = args.search?.trim();
  if (search) params.search = search;
  if (args.level && args.level !== 'all') params.cefr_level = args.level;
  if (args.official !== undefined) params.official = args.official;

  const { data } = await api.get<BackendPage<BackendModule>>('/modules/all', { params });
  const items = data.items.map(mapBackendModule);
  items.forEach((m) => cache.set(m.id, m));

  return {
    items,
    total: data.total,
    page: data.page,
    pageSize: data.size,
    pages: data.pages,
  };
}

export async function fetchPublicModuleById(id: string): Promise<PublicModule | null> {
  // Реальный публичный модуль по id (для ссылки «Поделиться» и страницы превью).
  // Приватный/несуществующий → бэк отдаёт MODULE_NOT_FOUND → возвращаем null.
  try {
    const { data } = await api.get<BackendModule>(`/modules/public/${id}`);
    const mapped = mapBackendModule(data);
    cache.set(id, mapped);
    return mapped;
  } catch {
    return null;
  }
}

export async function forkPublicModule(id: string): Promise<ModuleSummary> {
  try {
    const { data } = await api.post<BackendModule>(`/modules/${id}/fork`);
    cache.delete(id); // is_added для этого модуля изменился
    return mapForkedToSummary(data);
  } catch (err) {
    if (err instanceof AxiosError) {
      const code = (err.response?.data as { error?: { code?: string } })?.error?.code;
      if (code === 'MODULE_ALREADY_ADDED') throw new ForkModuleError('ALREADY_ADDED');
      if (code === 'MODULE_NOT_FOUND') throw new ForkModuleError('NOT_FOUND');
      if (!err.response) throw new ForkModuleError('NETWORK');
    }
    throw new ForkModuleError('UNKNOWN');
  }
}
